import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";

import {
  Box,
  Card,
  TableContainer,
  Table,
  TableBody,
  TableHead,
  TableRow,
  CardContent,
  CardHeader,
} from "@mui/material";

import { ActionButton } from "components/ActionButton";
import { DeleteDialog } from "components/DeleteDialog";
import { DeleteLine } from "components/EditPage/DeleteLine";
import { LineItemText } from "components/formFields/LineItemText";
import { LineTableCell } from "components/formFields/LineTableCell";
import { LineColumnHeading } from "components/formFields/line/LineColumnHeading";
import { FormErrors } from "components/utility/FormErrors";
import { Loading } from "components/utility/Loading";
import { VSpace } from "components/utility/VSpace";

import { i18n } from "services/i18nService";
import { getUpsAccounts } from "services/sosInventoryService/sosApi";

import { useErrors } from "hooks/useErrors";

import { AddAccountDialog } from "views/UpsAccount/AddAccountDialog";
import { UpsTrademark } from "views/UpsShipments/UpsShipment/UpsTrademark";

import {
  loadingIndicatorOn,
  loadingIndicatorOff,
} from "globalState/loadingSlice";

import { OBJECT_TYPES } from "appConstants";

const objectType = OBJECT_TYPES.UPS_ACCOUNT.fullString;

export function UpsAccount() {
  const { state } = useLocation();
  const [upsAccounts, setUpsAccounts] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteId, setDeleteId] = useState(false);
  const { errors, setErrors } = useErrors();

  const isLoading = useSelector(({ loading }) => loading.list);

  const dispatch = useDispatch();
  const recordsChanged = useSelector((state) => state.recordsChanged);

  useEffect(() => {
    if (state?.authError) {
      setErrors({ messages: [state?.authError] });
    }
  }, [state?.authError, setErrors]);

  useEffect(() => {
    async function retrieveRecords() {
      dispatch(loadingIndicatorOn());
      const { success, data } = await getUpsAccounts();
      if (success) {
        setUpsAccounts(data);
      }
      dispatch(loadingIndicatorOff());
    }
    retrieveRecords();
  }, [dispatch, recordsChanged]);

  return (
    <>
      <Box sx={{ display: "grid", minWidth: "1070px" }} m={2} mb={0}>
        <Box sx={{ position: "relative", margin: -2 }}>
          {isLoading && <Loading />}
        </Box>
        <Card variant="outlined" sx={{ maxWidth: 500 }}>
          <CardHeader
            sx={{ pb: 0 }}
            title={i18n("upsAccount.LinkedUpsAccounts")}
          />
          <CardContent>
            <FormErrors errors={errors} setErrors={setErrors} />
            <TableContainer
              sx={{ overflowX: "initial" }}
              data-testing="lineItems"
            >
              <Table size="small" padding="none" stickyHeader>
                <TableHead>
                  <TableRow>
                    <LineColumnHeading labelCode="UpsAccount" />
                    <LineColumnHeading labelCode="Description" />
                    <LineColumnHeading sx={{ displayPrint: "none" }} />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {upsAccounts?.map(({ number, description, id }, index) => {
                    return (
                      <TableRow key={index}>
                        <LineTableCell sx={{ width: "20.0rem" }}>
                          <LineItemText value={number} />
                        </LineTableCell>
                        <LineTableCell sx={{ width: "20.0rem" }}>
                          <LineItemText value={description} />
                        </LineTableCell>
                        <DeleteLine onClick={() => setDeleteId(id)} />
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>

              <Table size="small" padding="none" stickyHeader></Table>
            </TableContainer>
            <VSpace space={2} />
            <ActionButton onClick={() => setDialogOpen(true)}>
              {i18n("upsAccount.AddAccount")}
            </ActionButton>
          </CardContent>
        </Card>
        <VSpace space={4} />
        <UpsTrademark />
      </Box>
      <AddAccountDialog
        open={dialogOpen}
        handleClose={() => setDialogOpen(false)}
      />
      {Boolean(deleteId) && (
        <DeleteDialog
          open
          deleteSingle
          objectType={objectType}
          objectId={deleteId}
          message={i18n("global.deleteRecordMessage", {
            objectType: i18n(`objectType.${objectType}.Lower`),
            identifier: upsAccounts.find(({ id }) => id).number,
          })}
          onClose={() => setDeleteId(false)}
        />
      )}
    </>
  );
}
